<template>
  <div
    class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden"
    data-index="1"
  >
    <div class="tw:flex tw:justify-between tw:p-2 tw:px-4">
      <Checkbox
        v-model="selectAll"
        data-testid="select-all-checkbox"
        dense
        :label="selectAllLabel"
        new-design
      />

      <div class="tw:flex tw:items-center tw:gap-2">
        <div>
          {{ getLocaleText("outreach_search_tab.sort_by_label") }}
        </div>

        <BaseButtonGroup>
          <BaseButtonDropdown
            bordered
            chevron
            close-on-click
            color="white"
            data-testid="sort-dropdown"
            :disabled="loading"
            :label="sortLabel"
            new-design
            new-dropdown
            placement="bottom-start"
          >
            <BaseButtonList
              data-testid="sort-dropdown-list"
              :model-value="sortOptionValue"
              :options="sortOptions"
              @update:model-value="handleSortOption"
            />
          </BaseButtonDropdown>

          <BaseButton
            bordered
            color="white"
            data-testid="toggle-sort-button"
            :disabled="loading"
            :icon="sortByIcon"
            icon-size="sm"
            new-design
            new-icon
            @click="toggleSortDirection"
          />
        </BaseButtonGroup>
      </div>
    </div>
    <LoadingBar
      v-if="loading"
      height="1"
    />
    <BaseSeparator v-else />
    <div class="tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden">
      <AISuggestedContactsList
        :contacts="contacts"
        :selected-ids="selectedIds"
        @contact="toggleContact"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AISuggestedContactsList from "shared/components/AI/AISuggestedContacts/AISuggestedContactsList.vue";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseButtonGroup,
  BaseButtonList,
  BaseSeparator,
} from "shared/components/base";
import LoadingBar from "shared/components/base/LoadingBar.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { type SuggestedContactOptions } from "shared/composables/useSuggestedContacts";
import { type Contact } from "shared/resources";

interface AISuggestedContactsSelectionProps {
  contacts: Contact[];
  loading?: boolean;
}

interface SortOption {
  value: SuggestedContactOptions["sortField"];
  label: string;
}

const selectedIds = defineModel<string[]>("selectedIds", { default: () => [] });

const sortDirection = defineModel<SuggestedContactOptions["sortDirection"]>(
  "sortDirection",
  {
    default: "desc",
  }
);

const sortField = defineModel<SuggestedContactOptions["sortField"]>(
  "sortField",
  {
    default: "relevance",
  }
);

const props = defineProps<AISuggestedContactsSelectionProps>();

const sortOptions = computed<SortOption[]>(() => [
  {
    value: "relevance",
    label: getLocaleText("outreach.contact_sort_options.relevance"),
  },
  { value: "name", label: getLocaleText("outreach.contact_sort_options.name") },
]);

const sortByIcon = computed(() =>
  sortDirection.value === "desc" ? "sort-down" : "sort-up"
);

const sortOptionValue = computed<SortOption | undefined>(() =>
  sortOptions.value.find((option) => option.value === sortField.value)
);

const sortLabel = computed<string>(() =>
  sortOptionValue.value
    ? sortOptionValue.value.label
    : getLocaleText("outreach.contact_sort_options.relevance")
);

function handleSortOption(option: SortOption) {
  sortField.value = option.value;
}

const contactDocumentIds = computed<string[]>(() =>
  props.contacts.map((contact) => contact.documentId)
);

const selectAll = computed({
  get() {
    return (
      props.contacts.length &&
      props.contacts.length === selectedIds.value.length
    );
  },
  set(value) {
    if (value) {
      selectedIds.value = [...contactDocumentIds.value];
    } else {
      selectedIds.value = [];
    }
  },
});

const selectAllLabel = computed(() => `Select all (${props.contacts.length})`);

function toggleContact(documentId: string) {
  if (selectedIds.value.includes(documentId)) {
    selectedIds.value = selectedIds.value.filter((id) => id !== documentId);
  } else {
    selectedIds.value = selectedIds.value.concat(documentId);
  }
}

function toggleSortDirection() {
  sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
}
</script>
